import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  string,
  bool,
  arrayOf,
  shape,
  objectOf,
  number,
  oneOf,
} from "prop-types";

import cx from "classnames";

import initialState from "../data/initialState.js";
import fetchSearchResults from "../helpers/fetchSearchResults.js";
import fetchAuthSearchResults from "../helpers/fetchAuthSearchResults.js";
import {
  toggleFacet,
  clearFacetGroup,
  initFacets,
  initCurrentParams,
  createQueryString,
} from "../helpers/dynamicSearch.js";
import { scrollToId } from "../../../common/scrollHelpers.js";

import ReadingPaneModal from "../../ReadingPane/ReadingPaneModal.jsx";
import SelectedFilters from "../components/SelectedFilters/SelectedFilters.jsx";
import KeywordSearch from "../components/KeywordSearch/KeywordSearch.jsx";
import ResultsInfo from "../components/ResultsInfo/ResultsInfo.jsx";
import SimpleList from "../components/SimpleList/SimpleList.jsx";
import LoadMore from "../components/LoadMore/LoadMore.jsx";
import Pagination from "../components/Pagination/Pagination.jsx";
import Container3070 from "../../Container/Container3070.jsx";
import ContainerFull from "../../Container/ContainerFull.jsx";
import Facets from "./subcomponents/Facets.jsx";
import Results from "./subcomponents/Results.jsx";
import AIOverview from "./subcomponents/AIOverview.jsx";
import AIOverviewLoading from "./subcomponents/AIOverviewLoading.jsx";

const propTypes = {
  id: string.isRequired,
  pagination: bool,
  loadMore: bool,
  hideMobileSummary: bool,
  url: string.isRequired,
  featuredUrl: string,
  aiSummaryUrl: string,
  aiDatasourceId: string,
  showFacets: bool,
  filteredSearch: bool,
  schemaData: shape({
    inDefinedTermSet: arrayOf(
      shape({
        Type: string,
        Name: string,
        Url: string,
      }),
    ),
  }),
  dictionary: objectOf(string).isRequired,
  query: shape({
    listingId: string,
    showSummaries: bool,
    showDates: bool,
    showContentTypes: bool,
    showImages: string,
    excludedTaxonomy: string,
    loadAllPages: bool,
    pageId: string,
    perPage: number,
  }).isRequired,
  sorters: arrayOf(
    shape({
      isActive: bool,
      value: string,
      direction: oneOf(["asc", "desc"]),
    }),
  ),
};

const SearchApp = props => {
  const {
    id,
    hideMobileSummary,
    url,
    dictionary,
    query,
    sorters,
    pagination,
    loadMore,
    featuredUrl,
    aiSummaryUrl,
    aiDatasourceId,
    showFacets = true,
    filteredSearch = false,
    schemaData = null,
    isGated = false,
  } = props;

  const summaryUrl = aiSummaryUrl || '';
  const datasourceId = aiDatasourceId || '';


  const activeSorterParam = useMemo(() => {
    const activeSorter = sorters.find(sorter => sorter.isActive) || null;

    return activeSorter
      ? {
          sortBy: activeSorter.value,
          sortOrder: activeSorter.direction,
        }
      : null;
  }, [sorters]);

  const generalParams = {
    ...initialState.query,
    ...query,
    ...activeSorterParam,
  };

  const [showOverlay, setShowOverlay] = useState(false);
  const [mobileFacetsShown, setMobileFacetsShown] = useState(false);
  const [perPage, setPerPage] = useState(query.perPage || 4);

  const [selectedFacets, setSelectedFacets] = useState(
    initFacets(window.location.search),
  );
  const [serverData, setServerData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);


  //initialize the query to that of the query string if it exists
  const [currentParams, setCurrentParams] = useState(
    initCurrentParams(window.location.search, generalParams),
  );

  // Memoize the search query string to prevent unnecessary re-triggers
  const searchQuery = useMemo(() => (currentParams.q || '').trim(), [currentParams.q]);

  // Removed the useEffect that called initSelectDropdown()
  // since the Select component now handles React callback functionality internally

  const resultsFocusRef = useRef();
  const resultsWrapperRef = useRef();
  const showFocusRef = useRef({ showFocus: false });

  useEffect(() => {
    if (new URL(document.location).searchParams.get("q")) {
      scrollToId(id, true);
      setTimeout(() => {
        document.querySelector(`#${id} #site-search`).focus();
      }, 1000);
    }
  }, [id]);

  //update the url with the latest query parameters
  useEffect(() => {
    const queryString = createQueryString(currentParams, selectedFacets);

    if (queryString.length > 0) {
      window.history.replaceState(
        "",
        "",
        window.location.origin +
          window.location.pathname +
          "?" +
          createQueryString(currentParams, selectedFacets) +
          window.location.hash,
      );
    } else {
      window.history.replaceState(
        "",
        "",
        window.location.origin +
          window.location.pathname +
          window.location.hash,
      );
    }
  }, [currentParams, selectedFacets]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setShowOverlay(true);

    fetchSearchResults({
      url,
      featuredUrl,
      params: {
        ...currentParams,
        ...selectedFacets,
      },
      signal,
    })
      .then(function (data) {
        if (data && loadMore && currentParams.page > 1) {
          setServerData(prevState => {
            return {
              ...data,
              results: [...prevState.results, ...data.results],
            };
          });
        } else {
          setServerData(data);
        }
        setPerPage(parseInt(data.request.perPage, 10));
      })
      .catch(function (e) {
        if (e.name === "AbortError") {
          return; // Continuation logic has already been skipped, so return normally
        }
        // console.warn("request error: " + e.message);
      })
      .finally(function () {
        setShowOverlay(false);
      });
    // unmounts or data changes
    return () => {
      controller.abort();
    };
  }, [currentParams, selectedFacets, url, loadMore, featuredUrl]);

  // Separate useEffect for AI Summary via backend proxy
  useEffect(() => {
    if (isGated || !searchQuery || !summaryUrl || !datasourceId) {
      setSummaryData(null);
      setIsSummaryLoading(false);
      return;
    }

    const controller = new AbortController();
    const signal = controller.signal;

    setIsSummaryLoading(true);

    fetchAuthSearchResults({
      url: summaryUrl,
      params: { query: searchQuery, datasourceId: datasourceId },
      signal,
      headers: { "Content-Type": "application/json" }
    })
      .then(function (data) {
        const answer = data?.answer || null;
        const answerText = data?.answer?.answerText || data?.answerText || null;
        
        const citations = 
          data?.answer?.citations ||
          data?.answer?.citationMetadata?.citations ||
          data?.citations ||
          [];
        
        const references = 
          data?.answer?.references ||
          data?.references ||
          [];
        
        if (answerText) {
          setSummaryData({
            summary: {
              summaryWithMetadata: {
                summary: answerText,
                citationMetadata: {
                  citations: citations
                },
                references: references
              }
            }
          });
        } else if (answer) {
          if (answer.summaryWithMetadata) {
            const summaryWithMetadata = {
              ...answer.summaryWithMetadata,
              citationMetadata: {
                citations: citations.length > 0 ? citations : (answer.summaryWithMetadata?.citationMetadata?.citations || [])
              },
              references: references.length > 0 ? references : (answer.summaryWithMetadata?.references || [])
            };
            setSummaryData({ 
              summary: {
                ...answer,
                summaryWithMetadata
              }
            });
          } else if (typeof answer === 'string') {
            setSummaryData({
              summary: {
                summaryWithMetadata: {
                  summary: answer,
                  citationMetadata: { citations: citations },
                  references: references
                }
              }
            });
          } else {
            const summaryText = answer.text || answer.content || JSON.stringify(answer);
            setSummaryData({
              summary: {
                summaryWithMetadata: {
                  summary: summaryText,
                  citationMetadata: { citations: citations },
                  references: references.length > 0 ? references : (answer.references || [])
                }
              }
            });
          }
        } else {
          setSummaryData(null);
        }
      })
      .catch(function (e) {
        if (e.name === "AbortError") {
          return;
        }
        setSummaryData(null);
      })
      .finally(function () {
        setIsSummaryLoading(false);
      });

    return () => {
      controller.abort();
      setIsSummaryLoading(false);
    };
  }, [searchQuery, summaryUrl, datasourceId, isGated]);


  useEffect(() => {
    if (resultsFocusRef.current && showFocusRef.current.showFocus) {
      //move the focus back to the top of the results on page change after new results are rendred.
      window.setTimeout(() => {
        resultsFocusRef.current.focus();
        showFocusRef.current.showFocus = false;
      }, 100);
    }
  }, [serverData]);

  const handlePageChange = page => {
    showFocusRef.current.showFocus = true;
    setCurrentParams(prevState => {
      return { ...prevState, page: page };
    });
    if (pagination) {
      // timeout to allow for React to render change before scrolling
      setTimeout(() => {
        document
          .getElementById(`${id}-results`)
          .scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  const handleFacetChange = useCallback((facetGroupValue, facet) => {
    setCurrentParams(prevState => {
      return { ...prevState, page: 1 };
    });
    setSelectedFacets(prevState => {
      return toggleFacet(facetGroupValue, facet, { ...prevState });
    });
  }, []);

  const handleClearFacetGroup = facetGroupValue => {
    setCurrentParams(prevState => {
      return { ...prevState, page: 1 };
    });
    setSelectedFacets(prevState => {
      return clearFacetGroup(facetGroupValue, { ...prevState, page: 1 });
    });
  };

  const handleClearAllFacets = () => {
    setCurrentParams(prevState => {
      return { ...prevState, page: 1 };
    });
    setSelectedFacets({});
  };

  const handleSorterClick = params => {
    setCurrentParams(prevState => {
      return { ...prevState, ...params, page: 1 };
    });
  };

  const handleQueryUpdate = keyword => {
    const q = keyword === "" ? null : keyword;

    if (currentParams.q === q) {
      return;
    }

    setCurrentParams(prevState => {
      return { ...prevState, q, page: 1 };
    });
  };

  const clearQuery = () => {
    handleQueryUpdate("");
  };

  const showLoadMore =
    loadMore &&
    !pagination &&
    serverData &&
    serverData.totalResults > currentParams.page * perPage;

  // we should always show pagination if we have more than one page of results
  const showPagination = serverData && serverData.totalResults > perPage;

  const focusItemNumber = loadMore ? (currentParams.page - 1) * perPage : 0;

  const showResults =
    serverData && serverData.results && serverData.results.length > 0;
  const showFeatured =
    serverData && serverData.featured && serverData.featured.length > 0;
  const noResults = !showResults && !showFeatured && !showOverlay;

  // Generate schema markup for filtered search pages
  const renderSchemaScript = () => {
    if (!filteredSearch || !schemaData || !schemaData.inDefinedTermSet) {
      return null;
    }

    const schemaObject = {
      "@context": "https://schema.org",
      "@type": "DefinedTermSet",
      inDefinedTermSet: schemaData.inDefinedTermSet.map(term => ({
        "@type": term.type || term.Type || "DefinedTerm",
        name: term.name || term.Name,
        url: term.url || term.Url,
      })),
    };

    return (
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaObject) }}
      />
    );
  };

  return (
    <div
      className={`${filteredSearch ? "search__app search__app--filtered" : "search__app"}`}
      id={id}
    >
      {renderSchemaScript()}
      {!filteredSearch && (
        <div className="search__header">
          <h1 className="search__title">{`${dictionary.title || "Search results for"} "${currentParams.q}"`}</h1>
        </div>
      )}
      <div className="search__search-box">
        <KeywordSearch
          keywords={currentParams.q}
          handleEntry={handleQueryUpdate}
          loading={showOverlay}
          dictionary={dictionary}
        />
      </div>
      <ContainerFull>
        {!noResults &&
          serverData &&
          serverData.facets &&
          serverData.facets.length > 0 && (
            <>
              <SelectedFilters
                filters={serverData.facets}
                label={dictionary.selectedFiltersLabel}
                onClick={handleFacetChange}
                removeFilterLabel={dictionary.removeFilterLabel}
                selectedFacets={selectedFacets}
              />

              {showFacets && (
                <h2 className="filter-bar__title txt-h4">
                  {serverData.totalResults} Results{filteredSearch && " Found"}
                </h2>
              )}
            </>
          )}
      </ContainerFull>
      <Container3070
        className={cx("search__container", {
          "search__container--indent": !showFacets,
        })}
        left={
          <>
            {!noResults &&
              serverData &&
              showFacets &&
              serverData.facets &&
              serverData.facets.length > 0 &&
              serverData.results.length && (
                <Facets
                  serverData={serverData}
                  selectedFacets={selectedFacets}
                  dictionary={dictionary}
                  mobileFacetsShown={mobileFacetsShown}
                  setMobileFacetsShown={setMobileFacetsShown}
                  handleFacetChange={handleFacetChange}
                  filteredSearch={filteredSearch}
                  resultsWrapperRef={resultsWrapperRef}  
                  isGated={isGated}
                />
              )}
          </>
        }
        right={
          <React.Fragment>
            {/* {serverData && (
              <div className="search__info-row">
                <div className="search__results-info">
                  <ResultsInfo
                    totalResults={serverData.totalResults}
                    page={currentParams.page}
                    perPage={perPage}
                    resultsInfoLabel={dictionary.resultsInfoLabel}
                    query={currentParams.q}
                    isLoading={showOverlay}
                    clearAllFacetsLabel={dictionary.clearAll}
                  />
                </div>
              </div>
            )} */}
            <div className="search__no-results" aria-live="polite">
              {noResults && (
                <span>{dictionary.noResultsMessage || "No Results Found"}</span>
              )}
            </div> 
            {!isGated && (
              <ReadingPaneModal documentListing={resultsWrapperRef.current} />
            )}
            {!isGated && isSummaryLoading && (
              <AIOverviewLoading
                id={id}
                dictionary={dictionary}
              />
            )}
            {!isGated && !isSummaryLoading && summaryData && summaryData.summary && (
              <AIOverview
                serverData={summaryData}
                id={id}
                dictionary={dictionary}
              />
            )}
            {(showResults || showFeatured) && (
              <Results
                serverData={serverData}
                id={id}
                showOverlay={showOverlay}
                showDates={query.showDates}
                hideMobileSummary={hideMobileSummary}
                resultsWrapperRef={resultsWrapperRef}
                showFacets={showFacets}
                filteredSearch={filteredSearch}
                query={currentParams.q}
                clearQuery={clearQuery}
              />
            )}
            {showLoadMore && (
              <div className="search__load-more">
                <LoadMore
                  handleClick={() => handlePageChange(currentParams.page + 1)}
                  buttonClass="btn btn--primary search__load-more-btn"
                  dictionary={dictionary}
                />
              </div>
            )}
            {showPagination && (
              <div className="search__pagination">
                <Pagination
                  totalResults={serverData.totalResults}
                  resultsPerPage={perPage}
                  currentPage={currentParams.page}
                  handlePageChange={pageNum => handlePageChange(pageNum)}
                />
              </div>
            )}
          </React.Fragment>
        }
      />
    </div>
  );
};

SearchApp.propTypes = propTypes;
export default SearchApp;
